import { Text } from '@/shared/ui';
import { useTranslation } from 'react-i18next';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { SubscriptionDetail } from '@services/requests/housien-services/AgenciesService.subscription';

interface Props {
  subscription: SubscriptionDetail;
}

interface Notice {
  key: string;
  text: string;
  tone: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One notice the subscription may warrant, or null when it does not. */
type NoticeBuilder = (subscription: SubscriptionDetail, t: Translate) => Notice | null;

/**
 * A date for a notice, or a dash when there is none to show.
 *
 * @param value - The ISO date.
 * @returns The formatted date.
 */
function noticeDate(value: string | null | undefined): string {
  return formatLocalizedDate(value) || '—';
}

const NOTICE_BUILDERS: NoticeBuilder[] = [
  (subscription, t) => (subscription.data_freshness === 'stale'
    ? { key: 'stale', tone: 'var(--text-tertiary-soft)', text: t('subscription.plan.notices.stale') }
    : null),
  (subscription, t) => (subscription.consecutive_failures > 0
    ? {
      key: 'failures', tone: 'var(--accent-cobre)',
      text: t('subscription.plan.notices.failures', { count: subscription.consecutive_failures }),
    }
    : null),
  (subscription, t) => (subscription.cancel_at_period_end
    ? {
      key: 'cancelScheduled', tone: 'var(--accent-cobre)',
      text: t('subscription.plan.notices.cancelScheduled', { date: noticeDate(subscription.next_renewal_date) }),
    }
    : null),
  (subscription, t) => {
    const change = subscription.scheduled_plan_change;
    if (!change) return null;
    return {
      key: 'scheduled', tone: 'var(--accent-marinho)',
      text: t('subscription.plan.notices.scheduledChange', { plan: change.name, date: noticeDate(change.effective_at) }),
    };
  },
];

/**
 * Collects the notices the subscription itself warrants.
 *
 * @param subscription - The subscription.
 * @param t - Translation function.
 * @returns The notices, in order.
 */
function buildNotices(subscription: SubscriptionDetail, t: Translate): Notice[] {
  return NOTICE_BUILDERS
    .map((build) => build(subscription, t))
    .filter((notice): notice is Notice => notice !== null);
}

/**
 * Standing facts about the subscription that the strip has no room for.
 *
 * The scheduled plan change and the payment-failure count were previously only
 * visible with the plan comparison open; the staleness signal was typed and
 * rendered nowhere at all.
 *
 * @param props - Component props.
 * @returns The notices, or null when there are none.
 */
export function PlanNotices({ subscription }: Props) {
  const { t } = useTranslation('features/pricing');
  const notices = buildNotices(subscription, t);
  if (notices.length === 0) return null;

  return (
    <div className="flex flex-col gap-(--space-1)" data-testid="plan-notices">
      {notices.map((notice) => (
        <Text component="span"
          key={notice.key}
          data-testid={`plan-notice-${notice.key}`}
          style={{
            fontSize: 'var(--text-meta)', color: notice.tone,
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {notice.text}
        </Text>
      ))}
    </div>
  );
}
